import { createRouter, createWebHistory } from "vue-router";
import { useConfirm } from "primevue/useconfirm";

import usePermissions from "@/shared/composables/usePermissions";
import AppLayout from "@/shared/layout/AppLayout.vue";
import { useAppStore } from "@/shared/stores/appStore";

import useAuth from "@/domain/Auth/application/useAuth";
import { AuthRoutes } from "@/domain/Auth/presentation/routes/auth.routes";
import { CustomizationRoutes } from "@/domain/Customization/routes/customization.routes";
import { ExternalRoutes } from "@/domain/External/presentation/routes/external.routes";
import { FlowEditorRoutes } from "@/domain/Flows/FlowEditor/presentation/routes/flowEditor.routes";
import FlowFormRoutes from "@/domain/Flows/FlowForm/presentation/routes/flowForm.routes";
import { FlowListRoutes } from "@/domain/Flows/FlowList/presentation/routes/flowList.routes";
import { FlowsSharedRoutes } from "@/domain/Flows/shared/presentation/routes/flows.shared.routes";
import { IntegrationsRoutes } from "@/domain/Integrations/shared/routes/integrations.routes";
import { OrganizationsRoutes } from "@/domain/Organizations/presentation/routes/organizations.routes";
import { PlansRoutes } from "@/domain/Plans/presentation/routes/plans.routes";
import { QAToolsRoutes } from "@/domain/QATools/presentation/routes/qatools.routes";
import { UsersRoutes } from "@/domain/Users/presentation/routes/users.routes";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "index",
      redirect: () => {
        return "/admin";
      },
    },
    ...AuthRoutes,
    ...ExternalRoutes,
    ...FlowsSharedRoutes,
    ...FlowFormRoutes,
    {
      path: "/admin",
      component: AppLayout,
      children: [
        {
          path: "/admin",
          name: "adminIndex",
          redirect: () => {
            return "/admin/flows";
          },
        },
        ...FlowListRoutes,

        ...IntegrationsRoutes,
        ...CustomizationRoutes,
        ...QAToolsRoutes,
        ...UsersRoutes,
        ...OrganizationsRoutes,
        ...PlansRoutes,
        ...FlowEditorRoutes,
      ],
    },
    {
      path: "/debug",
      name: "debug",
      component: () => import("@/shared/pages/DebugPage.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/401",
      name: "unauthorized",
      component: () => import("@/shared/pages/errors/401Page.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/404",
      name: "notFound",
      component: () => import("@/shared/pages/errors/404Page.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: () => {
        return "/404";
      },
    },
  ],
});

router.beforeEach(async (to) => {
  const { isAuthenticated, getUserData } = useAuth();
  const appStore = useAppStore();
  const confirm = useConfirm();
  const { can } = usePermissions();

  // Close any open confirmation dialogs
  confirm.close();

  // if we are in some auth pages and the user is not authenticated, let the user log in
  if (
    (!isAuthenticated.value && to.name === "login") ||
    to.name === "forgotPassword" ||
    to.name === "resetPassword" ||
    to.name === "acceptInvitation"
  ) {
    return true;
  }

  // we are creating a new session from external URL, let the user in
  if (to.name === "extSessionCreate") {
    return true;
  }

  // we are doing a flow, let the user in
  if (to.name === "doFlow") {
    return true;
  }

  // we are debugging the editor, let the user in
  if (to.name === "debug") {
    return true;
  }

  // if we are in the login page and the user is authenticated, redirect to the admin page
  if (to.name === "login" && isAuthenticated.value) {
    return { name: "adminIndex" };
  }

  // If the user is not authenticated and the route requires authentication, redirect to the login page
  if (!isAuthenticated.value && to.meta.requiresAuth) {
    return { name: "login" };
  }

  await getUserData();

  // if the route contains editor, set the mode to editor
  if (to.name === "flowEditor") {
    appStore.setMode("editor");
  } else {
    appStore.setMode("panel");
    appStore.isMainNavOpen = true;
  }

  // If the url requires permission and the user is authenticated, check if the user has the required permissions
  if (to.meta.permissions) {
    if (!can(to.meta.permissions)) {
      return { name: "unauthorized" };
    }
  }
});

export default router;
